import { sortBy } from "lodash";
import { useEffect, useState } from "react";
import { fromEvent } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { Option } from "space-monad";
import { size } from "../../styles/media";
import { reduceObject } from "../../utils/utils";

type Size = keyof typeof size;
type Orientation = "portrait" | "landscape";

const foo: Array<[Size, number]> = reduceObject(
  size,
  (acc, val, size) => [...acc, [size, val]],
  []
);
const sorted = sortBy(foo, ([_, val]) => val);

export interface Configuration {
  size: keyof typeof size;
  orientation: Orientation;
  isMobile: boolean;
}

const getSize: () => Size = () =>
  Option(sorted)
    .map((s) => {
      return s.find(([_, val]) => window.innerWidth < val);
    })
    .map((x) => x[0])
    .getOrElse("desktop");

const getOrientation: () => Orientation = () =>
  window.innerHeight < window.innerWidth ? "portrait" : "landscape";

const allMobile: Array<Size> = [
  "mobileL",
  "mobileM",
  "mobileS",
  "tablet",
  "laptop",
  "md",
];

const isMobile: (size: Size) => boolean = (size: Size) =>
  allMobile.includes(size);

export function useWindowSize() {
  const [current, setCurrent] = useState<Configuration>(() => ({
    size: getSize(),
    orientation: getOrientation(),
    isMobile: isMobile(getSize()),
  }));

  useEffect(() => {
    const sub = fromEvent(window, "resize")
      .pipe(
        debounceTime(300),
        map(() => window.innerWidth * window.innerHeight),
        distinctUntilChanged(),
        map(() => {
          const size = getSize();
          return {
            size,
            orientation: getOrientation(),
            isMobile: isMobile(size),
          };
        })
      )
      .subscribe((x) => setCurrent(x));

    return () => sub.unsubscribe();
  }, []);

  return current;
}
